import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Image,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Icon,
  Divider,
} from '@chakra-ui/react';

import { DeleteIcon } from '@chakra-ui/icons';

import { getMarketplaceLogo } from 'components/marketplaceLogo';

import SaveButton from './SaveButton';
import { useColors } from './colors';

import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import { deleteStoreApi } from 'features/stores/stores.api';

const StoreDetails = ({ storeData, onStoreUpdate, hasChanges, onSave, isLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list: supplierOptions, getSuppliersListStatus } = useSelector(
    (state) => state.suppliers,
  );

  const { deleteStoreStatus } = useSelector((state) => state.stores);

  const [localStoreData, setLocalStoreData] = useState({
    name: '',
    username: '',
    marketplace: '',
  });

  const {
    cardBgColor,
    borderColor,
    inputColor,
    placeholderColor,
    textColor,
    inputBgColor,
    backgroundCardColor,
    hoverBgColor,
  } = useColors();

  useEffect(() => {
    dispatch(getSuppliersListApi());
  }, [dispatch]);

  useEffect(() => {
    if (storeData) {
      setLocalStoreData({
        name: storeData.name || '',
        username: storeData.username || '',
        marketplace: storeData.marketplace?.name || '',
      });
    }
  }, [storeData]);

  const currentLogo = getMarketplaceLogo(localStoreData.marketplace);

  const handleChange = (field, value) => {
    const updatedLocalData = {
      ...localStoreData,
      [field]: value,
    };
    setLocalStoreData(updatedLocalData);

    const updatedStore = {
      ...storeData,
      [field]: value,
    };
    onStoreUpdate(updatedStore);
  };

  const handleDeleteStore = async () => {
    if (!storeData?._id) return;

    try {
      await dispatch(deleteStoreApi(storeData._id)).unwrap();
      // Navigate to stores list after successful deletion
      navigate('/admin/stores');
    } catch (error) {
      console.error('Delete store failed:', error);
    }
  };

  const isDeleting = deleteStoreStatus === 'pending';

  return (
    <Box>
      <HStack align="start" spacing={8}>
        <Box width="30%" p={6} textAlign="center">
          <Text fontSize="lg" fontWeight="medium" mb={4} color={textColor}>
            Marketplace
          </Text>
          <Box borderRadius="lg" p={4}>
            <Image
              src={currentLogo}
              alt={localStoreData.marketplace}
              maxH="60px"
              mx="auto"
            />
          </Box>
        </Box>

        <Box width="70%" bg={cardBgColor} borderRadius="xl" p={6}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={6}>
              <FormControl>
                <FormLabel color={textColor}>Store Name</FormLabel>
                <Input
                  value={localStoreData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter store name"
                  size="md"
                  color={inputColor}
                  bg={inputBgColor}
                  borderColor={borderColor}
                  _placeholder={{ color: placeholderColor }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textColor}>Username</FormLabel>
                <Input
                  value={localStoreData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="Enter username"
                  size="md"
                  bg={inputBgColor}
                  color={inputColor}
                  borderColor={borderColor}
                  _placeholder={{ color: placeholderColor }}
                />
              </FormControl>
            </HStack>
            <FormControl isRequired>
              <FormLabel color={textColor}>Select Suppliers</FormLabel>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  bg={inputBgColor}
                  color={textColor}
                  borderColor={borderColor}
                  variant="outline"
                  w={{ base: '100%', md: 'auto' }}
                  size="lg"
                  borderRadius="lg"
                  minW="100%"
                  isDisabled={getSuppliersListStatus === 'pending'}
                  _hover={{
                    bg: hoverBgColor,
                  }}
                  _active={{
                    bg: hoverBgColor,
                  }}
                  _focus={{
                    bg: inputBgColor,
                    borderColor: 'blue.300',
                  }}
                  textAlign="left"
                >
                  {storeData?.suppliers?.length > 0
                    ? `${storeData?.suppliers?.length} supplier${storeData?.suppliers?.length > 1 ? 's' : ''}`
                    : 'Choose supplier(s)'}
                </MenuButton>

                <MenuList
                  bg={backgroundCardColor}
                  borderColor={borderColor}
                  p={3}
                  minWidth="200%"
                  shadow="lg"
                >
                  <MenuOptionGroup
                    title="Suppliers"
                    type="checkbox"
                    value={storeData.suppliers.map((s) => s.supplierId)}
                    onChange={(values) => {
                      onStoreUpdate({
                        ...storeData,
                        suppliers: values.map((id) => {
                          if (storeData.suppliers.some((s) => s.supplierId === id)) {
                            return storeData.suppliers.find((s) => s.supplierId === id);
                          }
                          return { supplierId: id };
                        }),
                      });
                    }}
                    color={textColor}
                    fontSize="md"
                  >
                    {supplierOptions.map((opt) => (
                      <MenuItemOption
                        fontSize="lg"
                        key={opt.value}
                        value={opt.value}
                        bg="transparent"
                        color={textColor}
                      >
                        {opt.label}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </FormControl>
            <Divider />
            <Button
              colorScheme="red"
              size="lg"
              leftIcon={<Icon as={DeleteIcon} />}
              onClick={handleDeleteStore}
              isLoading={isDeleting}
              loadingText="Deleting..."
              isDisabled={isDeleting}
            >
              Delete Store
            </Button>
          </VStack>
        </Box>
      </HStack>
      <SaveButton hasChanges={hasChanges} onSave={onSave} isLoading={isLoading} />
    </Box>
  );
};

export default StoreDetails;
